import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { FiDownload } from "react-icons/fi";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  createUnitsLedgerLog,
  exportLabDeliveryUnits,
  fetchUnitsLedgerEmployees,
  fetchUnitsLedgerLogs,
  updateUnitsLedgerLog,
  type LabDeliveryUnitsExport,
  type UnitsLedgerLog,
} from "../../store/superAdmin/unitsLedgerSlice";
import { fetchAllEmployees } from "../../store/allocation/employeeSlice";
import { fetchEmployeeUnits } from "../../store/superAdmin/dataExtractSlice";
import { employeeUnitsExcelRows } from "../../utils/employeeUnitsExcel";
import EmployeeNameEditor from "../../components/admin/EmployeeNameEditor";
import {
  AdminPageHeader,
  AdminPageShell,
  AdminPagination,
  AdminSearchInput,
  AdminSelect,
  AdminTableCard,
  AdminTableState,
  formatAdminDateTime,
} from "../../components/admin/AdminPageShell";
import {
  unitsTableDirtyInputClass,
  unitsTableInputClass,
  unitsTableTd,
  unitsTableTh,
} from "../../components/admin/unitsTableShared";
import { useToast } from "../../components/utilities/Toast";
import { caseHrefFromAllocationId } from "../../utils/caseIds";
import { CustomDatePicker } from "../../components/common/DatePickers";

const LIMIT = 25;
const UNIT_TYPES = ["Design", "QC", "Files", "Model"] as const;
const FAMILIES = [
  { value: "", label: "All families" },
  { value: "live", label: "Live" },
  { value: "redesign", label: "Redesign" },
  { value: "internal_live", label: "Internal live" },
  { value: "internal_redesign", label: "Internal redesign" },
] as const;

const EMP_COLS = [
  { key: "design_units_live", label: "D Live", title: "Designed live cases" },
  { key: "qc_units_live", label: "Q Live", title: "QC2 on live cases" },
  { key: "design_units_redesign", label: "D RD", title: "Designed redesigns" },
  { key: "qc_units_redesign", label: "Q RD", title: "QC2 on redesigns" },
  { key: "design_units_internal_live", label: "D IL", title: "Designed internal live" },
  { key: "qc_units_internal_live", label: "Q IL", title: "QC2 on internal live" },
  {
    key: "design_units_internal_redesign",
    label: "D IR",
    title: "Designed internal redesigns",
  },
  {
    key: "qc_units_internal_redesign",
    label: "Q IR",
    title: "QC2 on internal redesigns",
  },
  { key: "files_units", label: "Files", title: "Files units" },
  { key: "model_units", label: "Model", title: "Model units" },
] as const;

type LedgerTab = "employees" | "logs";
type LogEdit = { units?: number; unitsTo?: string; unitFor?: string };

const localIsoDate = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const defaultFromDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return localIsoDate(d);
};

const headerBtnClass =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition";
const headerBtnActiveClass =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-white text-[#2fa9bf] text-xs font-medium";
const familyBadgeClass = (family: string) => {
  if (family === "redesign") return "bg-amber-50 text-amber-800";
  if (family === "internal_live") return "bg-violet-50 text-violet-800";
  if (family === "internal_redesign") return "bg-rose-50 text-rose-800";
  return "bg-[#E8F6F8] text-[#1A6B78]";
};

const downloadLabUnitsExcel = (data: LabDeliveryUnitsExport) => {
  const labRows = data.labs.map((row) => ({
    Token: row.lab_token,
    Lab: row.lab_name,
    Organisation: row.organisation,
    "Live cases": row.live_cases,
    "Live units": row.live_units,
    "Redesign cases": row.redesign_cases,
    "Redesign units": row.redesign_units,
    "Total units": row.total_units,
  }));
  labRows.push({
    Token: "",
    Lab: "TOTAL",
    Organisation: "",
    "Live cases": data.totals.live_cases,
    "Live units": data.totals.live_units,
    "Redesign cases": data.totals.redesign_cases,
    "Redesign units": data.totals.redesign_units,
    "Total units": data.totals.total_units,
  });

  const caseRows = data.cases.map((row) => ({
    "Case ID": row.case_id,
    Type: row.type,
    Token: row.lab_token,
    Lab: row.lab_name,
    Created: row.created_at,
    "Delivery units": row.delivery_units,
    "Units by": row.units_by,
  }));

  const workbook = XLSX.utils.book_new();
  const labsSheet = XLSX.utils.json_to_sheet(labRows);
  labsSheet["!cols"] = [
    { wch: 10 },
    { wch: 28 },
    { wch: 28 },
    { wch: 12 },
    { wch: 12 },
    { wch: 16 },
    { wch: 16 },
    { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(workbook, labsSheet, "Lab totals");

  if (caseRows.length) {
    const casesSheet = XLSX.utils.json_to_sheet(caseRows);
    casesSheet["!cols"] = [
      { wch: 22 },
      { wch: 12 },
      { wch: 10 },
      { wch: 28 },
      { wch: 18 },
      { wch: 16 },
      { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, casesSheet, "Cases");
  }

  XLSX.writeFile(
    workbook,
    `Lab_Delivery_Units_${data.fromDate}_to_${data.toDate}.xlsx`,
  );
};

const UnitsLedger = () => {
  const dispatch = useAppDispatch();
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { user } = useAppSelector((state) => state.auth);
  const { allEmployees } = useAppSelector((state) => state.employee);
  const {
    employees,
    logs,
    page: activePage,
    totalPages,
    total,
    employeesLoading,
    logsLoading,
    exporting,
    saving,
    error,
  } = useAppSelector((state) => state.unitsLedger);
  const { showToast } = useToast();

  const [tab, setTab] = useState<LedgerTab>("employees");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(() => localIsoDate());
  const [empName, setEmpName] = useState("");
  const [family, setFamily] = useState("");
  const [unitFor, setUnitFor] = useState("");
  const [edits, setEdits] = useState<Record<number, LogEdit>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addCaseId, setAddCaseId] = useState("");
  const [addEmp, setAddEmp] = useState("");
  const [addFor, setAddFor] = useState<(typeof UNIT_TYPES)[number]>("Design");
  const [addUnits, setAddUnits] = useState("1");
  const [addPatient, setAddPatient] = useState("");
  const [addError, setAddError] = useState("");
  const [employeeExporting, setEmployeeExporting] = useState(false);

  const actor =
    user?.role === "Super Admin"
      ? user.user_name
      : employee?.emp_name || "System";

  const employeeOptions = useMemo(
    () =>
      [...(allEmployees || [])]
        .filter((emp) => emp.verified)
        .sort((a, b) => a.emp_name.localeCompare(b.emp_name)),
    [allEmployees],
  );

  const loading = tab === "employees" ? employeesLoading : logsLoading;
  const rangeQuery = useMemo(
    () => ({
      page,
      limit: LIMIT,
      search,
      fromDate,
      toDate,
      empName: tab === "logs" ? empName : undefined,
      family: tab === "logs" ? family : undefined,
      unitFor: tab === "logs" ? unitFor : undefined,
    }),
    [page, search, fromDate, toDate, empName, family, unitFor, tab],
  );

  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (tab === "employees") {
      dispatch(fetchUnitsLedgerEmployees(rangeQuery));
    } else {
      dispatch(fetchUnitsLedgerLogs(rangeQuery));
      setEdits({});
    }
  }, [dispatch, tab, rangeQuery]);

  const resetPage = () => setPage(1);

  const changeTab = (next: LedgerTab) => {
    setTab(next);
    setPage(1);
  };

  const normalizeType = (value: string) =>
    UNIT_TYPES.find((type) => type.toLowerCase() === value.toLowerCase()) ||
    value;

  const currentUnits = (log: UnitsLedgerLog) =>
    edits[log.id]?.units ?? log.units;
  const currentTo = (log: UnitsLedgerLog) =>
    edits[log.id]?.unitsTo ?? log.unitsTo;
  const currentFor = (log: UnitsLedgerLog) =>
    normalizeType(edits[log.id]?.unitFor ?? log.unitFor);
  const isDirty = (log: UnitsLedgerLog) =>
    currentUnits(log) !== log.units ||
    currentTo(log) !== log.unitsTo ||
    currentFor(log) !== normalizeType(log.unitFor);

  const openEmployeeLogs = (name: string) => {
    setEmpName(name);
    setSearchInput("");
    setSearch("");
    setPage(1);
    setTab("logs");
  };

  const handleSaveLog = async (log: UnitsLedgerLog) => {
    if (!isDirty(log)) return;
    try {
      setSavingId(log.id);
      await dispatch(
        updateUnitsLedgerLog({
          id: log.id,
          units: currentUnits(log),
          unitsTo: currentTo(log),
          unitFor: currentFor(log),
          units_By: actor,
        }),
      ).unwrap();
      setEdits((prev) => {
        const next = { ...prev };
        delete next[log.id];
        return next;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  const handleExportEmployeeUnits = async () => {
    if (fromDate > toDate) {
      showToast("From date cannot be after to date", "error");
      return;
    }
    setEmployeeExporting(true);
    try {
      const rows = await dispatch(
        fetchEmployeeUnits({ fromDate, toDate }),
      ).unwrap();
      if (!rows?.length) {
        showToast("No employees found for the selected period", "error");
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(employeeUnitsExcelRows(rows));
      worksheet["!cols"] = [
        { wch: 22 },
        { wch: 18 },
        { wch: 12 },
        { wch: 10 },
        { wch: 14 },
        { wch: 12 },
        { wch: 16 },
        { wch: 14 },
        { wch: 12 },
        { wch: 10 },
        { wch: 12 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Units");
      XLSX.writeFile(workbook, `Employee_Units_${fromDate}_to_${toDate}.xlsx`);
      showToast("Export downloaded successfully", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to export employee units", "error");
    } finally {
      setEmployeeExporting(false);
    }
  };

  const handleExportLabUnits = async () => {
    if (fromDate > toDate) {
      showToast("From date cannot be after to date", "error");
      return;
    }
    try {
      const data = await dispatch(
        exportLabDeliveryUnits({ fromDate, toDate }),
      ).unwrap();
      if (!data.labs.length) {
        showToast("No marked delivery units in this date range", "error");
        return;
      }
      downloadLabUnitsExcel(data);
      showToast(
        `Exported ${data.totals.total_units} units across ${data.totals.labs} labs`,
        "success",
      );
    } catch (err) {
      showToast(
        typeof err === "string" ? err : "Failed to export lab units",
        "error",
      );
    }
  };

  const handleAddCredit = async () => {
    setAddError("");
    const units = Number(addUnits);
    if (!addCaseId.trim() || !addEmp.trim() || !Number.isFinite(units) || units <= 0) {
      setAddError("Case ID, employee, and a positive units value are required.");
      return;
    }
    try {
      await dispatch(
        createUnitsLedgerLog({
          case_id: addCaseId.trim(),
          unitsTo: addEmp.trim(),
          unitFor: addFor,
          units,
          units_By: actor,
          patientName: addPatient.trim() || undefined,
        }),
      ).unwrap();
      setAddCaseId("");
      setAddEmp("");
      setAddFor("Design");
      setAddUnits("1");
      setAddPatient("");
      setAddOpen(false);
      dispatch(fetchUnitsLedgerLogs(rangeQuery));
    } catch (err) {
      setAddError(typeof err === "string" ? err : "Failed to add unit credit");
    }
  };

  if (user?.role !== "Super Admin" && employee?.role === "CRM") {
    return <Navigate to="/dashboard/profile" replace />;
  }

  const empColCount = 5 + EMP_COLS.length;
  const logColCount = 9;

  return (
    <>
      <Helmet>
        <title>Units Ledger</title>
      </Helmet>

      <AdminPageShell className="flex h-full min-h-0 flex-col gap-3 overflow-hidden text-[1.05rem]">
        <AdminPageHeader
          title="Units Ledger"
          subtitle="Employee credits by log date. Employee export uses this date range. Lab export uses case creation date and only marked delivery units."
        >
          <div className="flex rounded-xl overflow-hidden border-2 border-white">
            <button
              type="button"
              onClick={() => changeTab("employees")}
              className={`${tab === "employees" ? headerBtnActiveClass : headerBtnClass} rounded-none border-0`}
            >
              Employees
            </button>
            <button
              type="button"
              onClick={() => changeTab("logs")}
              className={`${tab === "logs" ? headerBtnActiveClass : headerBtnClass} rounded-none border-0 border-l-2 border-white`}
            >
              Case logs
            </button>
          </div>
          <AdminSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder={
              tab === "employees"
                ? "Search employee…"
                : "Search case, employee, or marker…"
            }
          />
          <CustomDatePicker
            label="From"
            value={fromDate}
            max={toDate}
            variant="header"
            className="w-full sm:w-[150px]"
            onChange={(iso) => {
              setFromDate(iso);
              resetPage();
            }}
          />
          <CustomDatePicker
            label="To"
            value={toDate}
            min={fromDate}
            variant="header"
            className="w-full sm:w-[150px]"
            onChange={(iso) => {
              setToDate(iso);
              resetPage();
            }}
          />
          <button
            type="button"
            onClick={handleExportEmployeeUnits}
            disabled={employeeExporting}
            title="Captain, Assistant Captain, Senior Designer, and Designer unit types for this date range"
            className={headerBtnClass}
          >
            <FiDownload size={14} />
            {employeeExporting ? "Exporting…" : "Export employees"}
          </button>
          <button
            type="button"
            onClick={handleExportLabUnits}
            disabled={exporting}
            title="Marked delivery units by lab. Uses this date range as case created, and skips unmarked cases."
            className={headerBtnClass}
          >
            <FiDownload size={14} />
            {exporting ? "Exporting…" : "Export lab units"}
          </button>
          {tab === "logs" ? (
            <>
              <AdminSelect
                value={family}
                onChange={(value) => {
                  setFamily(value);
                  resetPage();
                }}
              >
                {FAMILIES.map((item) => (
                  <option key={item.value || "all"} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </AdminSelect>
              <AdminSelect
                value={unitFor}
                onChange={(value) => {
                  setUnitFor(value);
                  resetPage();
                }}
              >
                <option value="">All types</option>
                {UNIT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </AdminSelect>
              <button
                type="button"
                onClick={() => setAddOpen((open) => !open)}
                className={addOpen ? headerBtnActiveClass : headerBtnClass}
              >
                {addOpen ? "Close add" : "Add missed credit"}
              </button>
            </>
          ) : null}
          <a href="/dashboard/admin/units" className={headerBtnClass}>
            Case Units
          </a>
          <AdminPagination
            tone="onAccent"
            page={activePage}
            totalPages={totalPages}
            onPrev={() => setPage((p) => p - 1)}
            onNext={() => setPage((p) => p + 1)}
            disabled={loading}
          />
        </AdminPageHeader>

        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {empName && tab === "logs" ? (
          <div className="flex items-center gap-2 text-sm text-[#4A4A48]">
            <span>
              Showing credits for <strong>{empName}</strong>
            </span>
            <button
              type="button"
              onClick={() => {
                setEmpName("");
                resetPage();
              }}
              className="text-[#2fa9bf] hover:underline"
            >
              Clear
            </button>
          </div>
        ) : null}

        {addOpen && tab === "logs" ? (
          <AdminTableCard className="p-4">
            <div className="grid gap-3 md:grid-cols-5">
              <input
                value={addCaseId}
                onChange={(e) => setAddCaseId(e.target.value)}
                placeholder="Case ID (CK / RD / IL / IR)"
                className={unitsTableInputClass}
              />
              <EmployeeNameEditor
                value={addEmp}
                options={employeeOptions}
                allowMultiple={false}
                allowEmpty
                placeholder="Credit to"
                onChange={setAddEmp}
              />
              <select
                value={addFor}
                onChange={(e) =>
                  setAddFor(e.target.value as (typeof UNIT_TYPES)[number])
                }
                className={unitsTableInputClass}
              >
                {UNIT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                value={addUnits}
                onChange={(e) => setAddUnits(e.target.value)}
                className={unitsTableInputClass}
                placeholder="Units"
              />
              <input
                value={addPatient}
                onChange={(e) => setAddPatient(e.target.value)}
                placeholder="Patient (optional)"
                className={unitsTableInputClass}
              />
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={handleAddCredit}
                className="rounded-lg bg-[#2fa9bf] px-3 py-1.5 text-[14px] font-medium text-white hover:bg-[#2296aa] disabled:cursor-not-allowed disabled:bg-[#D8D8D2]"
              >
                {saving ? "Saving…" : "Add credit"}
              </button>
              {addError ? (
                <span className="text-sm text-rose-600">{addError}</span>
              ) : (
                <span className="text-xs text-[#8A8A85]">
                  {total} logs in this range
                </span>
              )}
            </div>
          </AdminTableCard>
        ) : null}

        <AdminTableCard className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-auto">
            {tab === "employees" ? (
              <table className="min-w-full table-fixed border-separate border-spacing-0">
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-[#E8E8E4]">
                    <th className={`${unitsTableTh} w-[16%]`}>Employee</th>
                    <th className={`${unitsTableTh} w-[10%]`}>Role</th>
                    <th className={`${unitsTableTh} w-[8%]`}>Shift</th>
                    <th className={`${unitsTableTh} w-[7%]`}>Total</th>
                    {EMP_COLS.map((col) => (
                      <th
                        key={col.key}
                        className={`${unitsTableTh} w-[5%]`}
                        title={col.title}
                      >
                        {col.label}
                      </th>
                    ))}
                    <th className={`${unitsTableTh} w-[6%]`}>Logs</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((row, index) => (
                    <tr
                      key={`${row.emp_name}-${row.emp_id || index}`}
                      className={`border-b border-[#E3E8E8] last:border-0 ${
                        index % 2 ? "bg-[#FDFBF2]/60" : "bg-white"
                      }`}
                    >
                      <td className={unitsTableTd}>
                        <button
                          type="button"
                          onClick={() => openEmployeeLogs(row.emp_name)}
                          className="text-left font-semibold text-[#2fa9bf] hover:underline"
                        >
                          {row.emp_name}
                        </button>
                      </td>
                      <td className={unitsTableTd}>{row.role || "—"}</td>
                      <td className={unitsTableTd}>{row.shift || "—"}</td>
                      <td className={`${unitsTableTd} font-semibold`}>
                        {row.total_units}
                      </td>
                      {EMP_COLS.map((col) => (
                        <td key={col.key} className={unitsTableTd}>
                          {row[col.key]}
                        </td>
                      ))}
                      <td className={unitsTableTd}>{row.log_count}</td>
                    </tr>
                  ))}
                  {!loading && employees.length === 0 ? (
                    <AdminTableState
                      colSpan={empColCount}
                      variant="empty"
                      message="No employee units in this date range"
                    />
                  ) : null}
                  {loading ? (
                    <AdminTableState
                      colSpan={empColCount}
                      variant="loading"
                      message="Loading…"
                    />
                  ) : null}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full table-fixed border-separate border-spacing-0">
                <thead className="sticky top-0 z-20">
                  <tr className="border-b border-[#E8E8E4]">
                    <th className={`${unitsTableTh} w-[12%]`}>When</th>
                    <th className={`${unitsTableTh} w-[12%]`}>Case</th>
                    <th className={`${unitsTableTh} w-[12%]`}>Patient</th>
                    <th className={`${unitsTableTh} w-[10%]`}>Family</th>
                    <th className={`${unitsTableTh} w-[10%]`}>Type</th>
                    <th className={`${unitsTableTh} w-[16%]`}>Credited to</th>
                    <th className={`${unitsTableTh} w-[8%]`}>Units</th>
                    <th className={`${unitsTableTh} w-[12%]`}>Marked by</th>
                    <th className={`${unitsTableTh} w-[8%]`}>Save</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => {
                    const dirty = isDirty(log);
                    return (
                      <tr
                        key={log.id}
                        className={`border-b border-[#E3E8E8] last:border-0 ${
                          index % 2 ? "bg-[#FDFBF2]/60" : "bg-white"
                        }`}
                      >
                        <td className={unitsTableTd}>
                          {formatAdminDateTime(log.creation_ts)}
                        </td>
                        <td className={unitsTableTd}>
                          <a
                            href={caseHrefFromAllocationId(log.case_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-[#2fa9bf] hover:underline break-all"
                          >
                            {log.case_id}
                          </a>
                        </td>
                        <td className={unitsTableTd}>
                          {log.patientName || "—"}
                        </td>
                        <td className={unitsTableTd}>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[12px] font-medium ${familyBadgeClass(
                              log.units_family,
                            )}`}
                          >
                            {log.units_family_label}
                          </span>
                        </td>
                        <td className={unitsTableTd}>
                          <select
                            value={currentFor(log)}
                            onChange={(e) =>
                              setEdits((prev) => ({
                                ...prev,
                                [log.id]: {
                                  ...prev[log.id],
                                  unitFor: e.target.value,
                                },
                              }))
                            }
                            className={
                              dirty ? unitsTableDirtyInputClass : unitsTableInputClass
                            }
                          >
                            {UNIT_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={unitsTableTd}>
                          <EmployeeNameEditor
                            value={currentTo(log)}
                            options={employeeOptions}
                            allowMultiple={false}
                            placeholder="Employee"
                            onChange={(value) =>
                              setEdits((prev) => ({
                                ...prev,
                                [log.id]: { ...prev[log.id], unitsTo: value },
                              }))
                            }
                          />
                        </td>
                        <td className={unitsTableTd}>
                          <input
                            type="number"
                            min={0}
                            value={currentUnits(log)}
                            onChange={(e) =>
                              setEdits((prev) => ({
                                ...prev,
                                [log.id]: {
                                  ...prev[log.id],
                                  units: Number(e.target.value),
                                },
                              }))
                            }
                            className={
                              dirty ? unitsTableDirtyInputClass : unitsTableInputClass
                            }
                          />
                        </td>
                        <td className={unitsTableTd}>{log.unitsBy || "—"}</td>
                        <td className={unitsTableTd}>
                          <button
                            type="button"
                            disabled={!dirty || savingId === log.id}
                            onClick={() => handleSaveLog(log)}
                            className="rounded-lg bg-[#2fa9bf] px-2.5 py-1 text-[14px] font-medium text-white hover:bg-[#2296aa] disabled:cursor-not-allowed disabled:bg-[#D8D8D2] disabled:text-[#8A8A85]"
                          >
                            {savingId === log.id ? "…" : "Save"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && logs.length === 0 ? (
                    <AdminTableState
                      colSpan={logColCount}
                      variant="empty"
                      message="No unit logs in this date range"
                    />
                  ) : null}
                  {loading ? (
                    <AdminTableState
                      colSpan={logColCount}
                      variant="loading"
                      message="Loading…"
                    />
                  ) : null}
                </tbody>
              </table>
            )}
          </div>
        </AdminTableCard>
      </AdminPageShell>
    </>
  );
};

export default UnitsLedger;
